import { useFragment } from "react-relay";
import { graphql } from "relay-runtime";
import { useTeamScheduleLayout_teamScheduleEventOccasion$key } from "./__generated__/useTeamScheduleLayout_teamScheduleEventOccasion.graphql";
import { getTeamScheduleListItemHeight } from "../TeamScheduleListItem/getTeamScheduleListItemHeight";
import { TeamScheduleSectionHeaderHeight } from "./TeamScheduleHeader/TeamScheduleSectionHeader";
import { useMemo } from "react";

export function useTeamScheduleLayout(props: {
  isActive: boolean;
  teamScheduleEventOccasions: useTeamScheduleLayout_teamScheduleEventOccasion$key;
  freq: "DAILY" | "WEEKLY" | "MONTHLY";
  startY: number;
  initialIndex: number;
}) {
  const teamScheduleEvents = useFragment(
    graphql`
      fragment useTeamScheduleLayout_teamScheduleEventOccasion on TeamScheduleEventOccasion
      @relay(plural: true) {
        ... on TeamScheduleInlineRoutineOccasion {
          __typename
          taskExecution {
            routineOccasion {
              __typename
              routine {
                description
                descriptionImages {
                  __typename
                }
              }
              assigned {
                id
              }
              assignedRoles {
                id
              }
            }
          }
        }
        ... on RoutineOccasion {
          __typename
          routine {
            description
            descriptionImages {
              __typename
            }
          }
          assigned {
            id
          }
          assignedRoles {
            id
          }
        }
      }
    `,
    props.teamScheduleEventOccasions
  );

  const headerOffset = props.startY + TeamScheduleSectionHeaderHeight;
  const offsets = useMemo(() => {
    const eventOffsets = teamScheduleEvents
      .flatMap((o) => {
        if (
          o.__typename === "TeamScheduleInlineRoutineOccasion" ||
          o.__typename === "RoutineOccasion"
        ) {
          return [o];
        }
        return [];
      })
      .reduce((acc, event, index) => {
        const occasion =
          event.__typename === "TeamScheduleInlineRoutineOccasion"
            ? event.taskExecution?.routineOccasion
            : event;

        const hasDescription =
          (occasion?.routine?.description?.length ?? 0) > 0;
        const hasDescriptionMedia =
          (occasion?.routine?.descriptionImages?.length ?? 0) > 0;
        const hasAssignedEmployees = (occasion?.assigned?.length ?? 0) > 0;
        const hasAssignedRoles =
          (occasion?.assignedRoles?.length ?? 0) > 0;
        const height = getTeamScheduleListItemHeight(
          props.freq,
          (hasAssignedEmployees || hasAssignedRoles) && (hasDescription || hasDescriptionMedia)
        );

        return [...acc, (index === 0 ? 0 : acc[index - 1]) + height];
      }, [] as number[])
      .map((offset) => offset + headerOffset);
    return [headerOffset, ...eventOffsets];
  }, [teamScheduleEvents, props.freq, headerOffset]);

  return {
    layout: {
      startY: props.startY,
      endY:
        props.isActive && offsets.length > 0
          ? offsets[offsets.length - 1]
          : props.startY,
      initialIndex: props.initialIndex,
    },
    offsets: props.isActive ? offsets : [],
  };
}
